/**
 * firestoreUtils.ts
 *
 * Small helpers for writing safe Firestore payloads.
 *
 * Why this file exists: Firestore strict-rejects `setDoc(..., { foo: undefined })`
 * with "Unsupported field value: undefined (found in document …)". An optional
 * field on a TypeScript interface that is left unset arrives as `undefined`,
 * not as a missing key — so spreading raw input objects into setDoc payloads
 * is a bug class. ENGINEERING.md §4 lists this as a refused pattern.
 *
 * Always pipe write-payloads through `omitUndefined` (or `pickDefined` for
 * partial updates with a known shape) BEFORE calling setDoc / addDoc /
 * batch.set / tx.update.
 */

/**
 * Returns a new object with keys whose value is exactly `undefined` removed.
 * Empty strings, `null`, `0`, and `false` are preserved — those are
 * intentional values, not "unset". Use this for full-document writes.
 */
export function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

/**
 * Like `omitUndefined`, but recursively. Useful when you're writing a nested
 * shape where any leaf could be undefined (e.g. score scaffolds from form
 * state).
 *
 * Arrays-of-objects ARE now scrubbed too — each element is passed back
 * through `omitUndefinedDeep`. That's required for EPR's bucket arrays
 * (okrItems / kpiItems / coreItems / leadershipItems) where individual
 * items legitimately have optional fields (notesEvidence, target, actual,
 * achievementPct, employeeSelfScore, managerScore, itemFinalScore — any
 * of which may be unset while the form is half-filled).
 *
 * Primitive-valued array elements pass through unchanged; arrays
 * containing `undefined` elements remain a programmer error (Firestore
 * still rejects them) — we only fix the common nested-object case.
 */
export function omitUndefinedDeep<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      (out as Record<string, unknown>)[k] = v.map(el =>
        (el !== null && typeof el === 'object' && !Array.isArray(el))
          ? omitUndefinedDeep(el as Record<string, unknown>)
          : el,
      );
    } else if (v !== null && typeof v === 'object') {
      (out as Record<string, unknown>)[k] = omitUndefinedDeep(v as Record<string, unknown>);
    } else {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}
